/* global analytics */
import { useState } from 'react'
import styled from 'styled-components'

import typeStyle from '../../styles/typeStyle'

import Vimeo from '../Vimeo'
import CrossOutIcon from '../icons/CrossOut'
import { formatPrice } from '../utils/pricing'

import Modal from './Modal'
import Button from './Button'
import InvisibleButton from './InvisibleButton'
import LazyImage from './LazyImage'
import StreamableVideo from './StreamableVideo'

const Wrapper = styled.div`
  background-color: white;
`

const Header = styled.header`
  position: absolute;
  z-index: 1;
  width: 100%;
  display: flex;
  justify-content: flex-end;

  height: 64px;
  padding: 16px;

  background-color: white;
  border-bottom: 1px solid #d5d5d5;
`

const CloseButton = styled(InvisibleButton)`
  width: 32px;
  height: 32px;
  color: ${({ theme }) => theme.brand.forest};

  svg {
    width: 100%;
    height: 100%;
  }
`

const Content = styled.div`
  height: 100%;
  padding: 16px;
  padding-top: 80px; /* Header Height + 16px */
  padding-bottom: 104px; /* Footer Height + 16px */

  overflow-x: hidden;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`

const Placeholder = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  margin-bottom: 16px;
  background-color: #ddd;

  &:last-child {
    margin-bottom: 0;
  }
`

const Footer = styled.footer`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  height: 88px;
  padding: 16px;

  background-color: white;
  border-top: 1px solid #d5d5d5;

  overflow-x: hidden;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`

const Details = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 16px;
`

const ListingTitle = styled.div`
  ${typeStyle('-ds-font-body-emphasised')}
  color: ${({ theme }) => theme.brand.forest};
  letter-spacing: 0.25px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ListingPrice = styled.div`
  ${typeStyle('-ds-font-body-light')}
  color: #999;
  letter-spacing: 0.25px;
`

const VideoContainer = styled(Placeholder)`
  padding-top: 56.25%;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
  }
`

interface MobileGalleryProps {
  photos?: Pick<GQL_Photo, 'id' | 'image' | 'aspectRatio'>[]
  videos?: Array<{
    id: string
    streamUrl: string
    thumbnailUrl: string
    posterUrl: string
  }>
  photoVersion?: 'hero' | 'large' | 'medium' | 'original' | 'thumb'
  listing?: {
    price: number
    nicePricingSuffix: string
    title: string
  }
  videoUrl?: string
  total?: number
  isOpen?: boolean
  onRequestClose: (...args: any) => any
  onBook?: (...args: any) => any
}

const MobileGallery = ({
  photos,
  videos = [],
  photoVersion = 'large',
  listing,
  videoUrl,
  total,
  isOpen,
  onRequestClose,
  onBook,
}: MobileGalleryProps) => {
  const [videoPlayed, setVideoPlayed] = useState(false)
  const handleVideoPlayed = (url: string) => {
    if (!videoPlayed) {
      setVideoPlayed(true)
      analytics.track('Played Video', { source: 'page', url })
    }
  }

  return (
    <Modal
      className="c-fullscreen-modal__content"
      overlayClassName="c-fullscreen-modal__overlay"
      onRequestClose={onRequestClose}
      isOpen={isOpen}
    >
      <Wrapper>
        <Header>
          <CloseButton onClick={onRequestClose}>
            <CrossOutIcon />
          </CloseButton>
        </Header>
        <Content>
          {videoUrl && (
            <VideoContainer>
              <Vimeo url={videoUrl} onPlay={handleVideoPlayed} />
            </VideoContainer>
          )}
          {videos?.map(({ id, streamUrl, posterUrl }) => (
            <Placeholder
              key={`photo-${id}`}
              style={{
                paddingTop: '75%',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                }}
              >
                <StreamableVideo
                  src={streamUrl}
                  poster={posterUrl}
                  browserControls
                />
              </div>
            </Placeholder>
          ))}
          {photos?.map(({ id, image, aspectRatio }) => (
            <Placeholder
              key={`photo-${id}`}
              style={{
                paddingTop: aspectRatio ? `${(1 / aspectRatio) * 100}%` : '75%',
              }}
            >
              <LazyImage data-src={image[photoVersion]} cover />
            </Placeholder>
          ))}
        </Content>
        {listing && (
          <Footer>
            <Details>
              <ListingTitle>{listing.title}</ListingTitle>
              <ListingPrice>
                {total && total > 0
                  ? `${formatPrice(total, 0)} total`
                  : `${formatPrice(listing.price, 0)} ${
                      listing.nicePricingSuffix
                    }`}
              </ListingPrice>
            </Details>
            <Button style={{ width: 136 }} onClick={onBook}>
              Book
            </Button>
          </Footer>
        )}
      </Wrapper>
    </Modal>
  )
}

export default MobileGallery
